import { useEffect, useMemo, useRef, useState } from "react";
import Select from "./Select.jsx";
import { useQuery } from "../lib/dataClient.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { resampleToRank, timeAtRankFrac } from "../lib/scanVolume.js";

// Sizes offered by the window-size control. The lens's own `limit` is folded in
// so its default is always selectable even if it isn't on the ladder.
const SIZE_LADDER = [120, 250, 500, 1000];

// Rank-space bars behind the brush — fixed count, the SVG stretches.
const RANK_BARS = 96;

const FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
function fmtTime(ms) { return Number.isFinite(ms) ? FMT.format(new Date(ms)) : "–"; }
// formatRange collapses the shared parts of a range ("Jul 17, 3:01 – 3:13 PM"
// for a same-day window) — two fully-spelled timestamps made the controls
// row wrap too easily once the prompts toggle joined it.
function fmtTimeRange(fromMs, toMs) {
  if (!Number.isFinite(fromMs)) return "–";
  if (!Number.isFinite(toMs)) return fmtTime(fromMs);
  return FMT.formatRange(new Date(Math.min(fromMs, toMs)), new Date(Math.max(fromMs, toMs)));
}
function traceTimeMs(t) {
  const v = t?.timestamp;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}
const clamp01 = (v) => Math.min(1, Math.max(0, v));

// Scan-window state for a lens that hydrates + scans a slice of the scope's
// prompts client-side, where accumulating the whole scope would be too costly —
// today the X-ray lens (XRayBody), which runs signal triggers over each hydrated
// prompt. Content lenses backed by durable signal_tags (skill/memory/tool detail)
// read bodies cheaply by id, so they use a growing load-more list instead.
//
// The window pages through the scope by RANK POSITION, not absolute time. At
// offset N the hook fetches the `windowSize` prompts ranked N..N+windowSize
// (newest-first) via a getTraces query with that offset — so the whole scope is
// reachable (not just a loaded page) AND every position holds prompts (no empty
// time gaps to scrub across). The brush maps its handle to the offset; the
// window commits on release so scans don't thrash mid-drag.
//
// Returns the window's trace ids/objects + a `scrubber` prop bag for
// <ScanWindowScrubber {...scrubber} /> (which fetches its own token-volume
// backdrop from scopePath/range — see getScanVolume).
export function useScanWindow({ scopePath, range, limit = 120, sort = "newest", scopeTotal = 0, sizeOptions, enabled = true, query = "" } = {}) {
  const [windowSize, setWindowSize] = useState(limit);
  const [offset, setOffset] = useState(0);       // committed rank offset (newest = 0)
  const [pendingFrac, setPendingFrac] = useState(null); // live drag handle fraction; null when idle

  // Signals have NO server-side scope predicate (signalsDim.rewriteScopeStep
  // drops them), so the window is the PARENT window regardless of which signal
  // is selected. Drop signal steps from the window query + key so drilling
  // between signals doesn't re-fetch a fresh (and, on live data, shifted) window
  // — the window stays pinned and the per-signal scan cache keeps matching.
  // Only an explicit change (scrubber / range / a real scope dim) re-fetches.
  const windowScope = useMemo(
    () => (scopePath || []).filter((s) => s.dim !== "signals" && s.dim !== "signal"),
    [scopePath],
  );
  const scopeKey = windowScope.map((s) => `${s.dim}:${(s.ids || [s.id]).join(",")}`).join("/");
  const rangeKey = typeof range === "object" ? `${range?.from}-${range?.to}` : String(range ?? "");
  useEffect(() => { setOffset(0); setPendingFrac(null); }, [scopeKey, rangeKey, query]);

  // Gate the sensitive enriched-prompt COLUMN out of this getTraces projection
  // for a role lacking ai_sessions.read.sensitive — exactly as ScopedPage's
  // trace query does. Without it the column policy 403s the WHOLE query, and
  // these scan surfaces (signals console, signal/skill/tool detail) never read
  // `error`, so a non-sensitive role gets a silent blank instead of the
  // non-sensitive window. Use hasPermission (fail-OPEN while caps
  // load/unavailable/degraded) so an admin mid-load still requests the full
  // projection; the column drops only once caps RESOLVE without the grant. The
  // gated prompt cells keep their per-row redaction affordance downstream.
  const caps = useMyPermissions();
  const canReadSensitivePrompts = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);

  // PIN the window: staleMs Infinity so a remount (every drill into a signal
  // unmounts/remounts this) does NOT revalidate to a fresh "newest N" — on live
  // data that shift would change the ids and force a re-scan. The window only
  // re-fetches when the query key changes — i.e. an EXPLICIT change to scope,
  // range, window size, scrubber offset, or sensitive-read posture (all part of
  // these params, which form the query cache key). That's exactly "re-fetch
  // only when I change the prompt window."
  // `enabled` gates the fetch while a caller is still RESOLVING the window's
  // scope (the X-ray substrate windows wait for the scope's tool entries) —
  // without it the hook would fetch an unfiltered window first and re-fetch
  // filtered a beat later, double-scanning every substrate tab.
  const { data: windowTraces = [], loading } = useQuery("getTraces", {
    scopePath: windowScope, range, limit: windowSize, sort, offset, canReadSensitivePrompts,
    // The scope steer: the window samples scope ∩ steer, so every X-ray
    // reading is oriented to what the steer keeps, not just the base scope.
    ...(query ? { query } : {}),
  }, { staleMs: Infinity, enabled });

  const total = Number(windowTraces?.total) || scopeTotal || windowTraces.length || 0;
  const maxOffset = Math.max(0, total - windowSize);
  const scrubbable = maxOffset > 0;

  const ids = useMemo(() => (enabled ? windowTraces.map((t) => t.id).filter(Boolean) : []), [windowTraces, enabled]);
  const idsKey = ids.join(",");

  const { winMin, winMax } = useMemo(() => {
    const times = windowTraces.map(traceTimeMs).filter((v) => v != null);
    if (!times.length) return { winMin: null, winMax: null };
    return { winMin: Math.min(...times), winMax: Math.max(...times) };
  }, [windowTraces]);

  // Handle fraction = newest edge of the window on a left=oldest / right=newest
  // rail. offset 0 (newest) → 1 (far right); offset maxOffset (oldest) → leftmost.
  const windowFrac = total > 0 ? Math.min(1, windowSize / total) : 1;
  const committedFrac = total > 0 ? clamp01(1 - offset / total) : 1;
  const displayFrac = pendingFrac != null ? pendingFrac : committedFrac;

  const commit = (frac) => {
    const f = typeof frac === "number" ? frac : displayFrac;
    const next = Math.min(maxOffset, Math.max(0, Math.round((1 - f) * total)));
    setOffset(next);
    setPendingFrac(null);
  };

  const options = useMemo(
    () => (sizeOptions || Array.from(new Set([limit, ...SIZE_LADDER])).sort((a, b) => a - b)),
    [sizeOptions, limit],
  );

  return {
    ids,
    idsKey,
    windowTraces,
    loading,
    // True from the first scrub movement until the re-targeted window's
    // traces land — covers both the live drag (pendingFrac) and the
    // post-commit fetch. Lens bodies key on this to cancel the running
    // scan immediately and swap the results table to its skeleton, so
    // the feedback for "you're choosing a new window" is instant rather
    // than the old scan running on until the new ids arrived.
    windowPending: pendingFrac != null || loading,
    scrubber: {
      scrubbable, displayFrac, windowFrac,
      onSeek: (f) => setPendingFrac(clamp01(Math.max(windowFrac, f))),
      commit,
      windowSize, setWindowSize, sizeOptions: options,
      offset, count: windowTraces.length, total, winMin, winMax, loading,
      dragging: pendingFrac != null,
      scopePath, range,
    },
  };
}

// The brush: a rank-domain rail (left = oldest, right = newest; equal width =
// equal prompt count, so the window keeps a CONSTANT width wherever it sits)
// with the scope's token volume drawn behind it as bars — resampled from time
// buckets into rank space, so a bursty hour stretches wide and a quiet weekend
// nearly vanishes. Grab the window to drag it; click outside it to jump there;
// arrows nudge by half a window (shift = full), Home/End = oldest/newest.
// Commits on release so the scan doesn't thrash mid-drag.
function VolumeBrush({ posFrac, windowFrac, onSeek, commit, disabled, bars, mode, loading, volumeLoading }) {
  const trackRef = useRef(null);
  const downRef = useRef(false);
  const grabRef = useRef(0);      // pointer frac − window right-edge frac at grab
  const latestRef = useRef(null); // last frac set — committed verbatim on release
  const [dragging, setDragging] = useState(false);

  const rightFrac = clamp01(posFrac);
  const leftFrac = Math.max(0, rightFrac - windowFrac);

  const maxBar = useMemo(() => bars.reduce((a, b) => Math.max(a, b), 0), [bars]);
  const hasBars = mode !== "empty" && maxBar > 0;

  function fracFromEvent(e) {
    const el = trackRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return clamp01((e.clientX - r.left) / Math.max(1, r.width));
  }
  function seek(frac) { latestRef.current = frac; onSeek?.(frac); }
  function onPointerDown(e) {
    if (disabled) return;
    downRef.current = true;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const f = fracFromEvent(e);
    if (f >= leftFrac && f <= rightFrac) {
      // Grab the window — drag moves it relative to where it was caught.
      grabRef.current = f - rightFrac;
    } else {
      // Jump the window's center to the pointer, then keep dragging.
      const next = clamp01(Math.max(windowFrac, Math.min(1, f + windowFrac / 2)));
      grabRef.current = f - next;
      seek(next);
    }
  }
  function onPointerMove(e) {
    if (!downRef.current) return;
    seek(clamp01(fracFromEvent(e) - grabRef.current));
  }
  function endDrag(e) {
    if (!downRef.current) return;
    downRef.current = false;
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    commit?.(latestRef.current);
  }
  function onKeyDown(e) {
    if (disabled) return;
    const step = (e.shiftKey ? 1 : 0.5) * windowFrac;
    if (e.key === "ArrowLeft") seek(Math.max(windowFrac, rightFrac - step));
    else if (e.key === "ArrowRight") seek(Math.min(1, rightFrac + step));
    else if (e.key === "Home") { seek(windowFrac); commit?.(windowFrac); }
    else if (e.key === "End") { seek(1); commit?.(1); }
    else return;
    e.preventDefault();
  }

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      onKeyUp={(e) => { if (e.key === "ArrowLeft" || e.key === "ArrowRight") commit?.(latestRef.current); }}
      role="slider"
      aria-label="Scan window. Drag to resize it or click the graph to move it. The graph shows prompt volume by rank."
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(rightFrac * 100)}
      tabIndex={disabled ? -1 : 0}
      className={[
        "group relative h-[26px] w-full select-none overflow-hidden rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] outline-none focus-visible:border-[color:var(--green)]",
        disabled ? "opacity-60" : dragging ? "cursor-grabbing" : "cursor-pointer",
      ].join(" ")}
    >
      {/* token-volume bars, rank domain (left = oldest) */}
      {hasBars ? (
        <svg
          viewBox={`0 0 ${bars.length} 100`}
          preserveAspectRatio="none"
          aria-hidden="true"
          className={["absolute inset-0 h-full w-full", volumeLoading ? "opacity-40" : ""].join(" ")}
        >
          {bars.map((v, i) => {
            if (v <= 0) return null;
            const h = Math.max(3, (v / maxBar) * 92);
            const center = (i + 0.5) / bars.length;
            const inWindow = center >= leftFrac && center <= rightFrac;
            return (
              <rect
                key={i}
                x={i + 0.12}
                width={0.76}
                y={100 - h}
                height={h}
                fill={inWindow ? "var(--green)" : "var(--line-strong)"}
                opacity={inWindow ? 0.9 : 0.55}
              />
            );
          })}
        </svg>
      ) : (
        // No backdrop (empty scope / inexpressible filter / still loading) —
        // degrade to the plain rail so the brush still reads.
        <div className={["pointer-events-none absolute inset-x-[6px] top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-[color:var(--line)]", volumeLoading ? "animate-pulse" : ""].join(" ")} />
      )}
      {/* the window */}
      <div
        className={[
          "pointer-events-none absolute inset-y-0 border-x-[2px] border-[color:var(--green)]",
          loading ? "animate-pulse" : "",
        ].join(" ")}
        style={{
          left: `${leftFrac * 100}%`,
          width: `${Math.max(0.4, (rightFrac - leftFrac) * 100)}%`,
          // color-mix keeps the bars readable through the window tint.
          background: `color-mix(in srgb, var(--green-soft) ${dragging ? 60 : 40}%, transparent)`,
        }}
      />
    </div>
  );
}

// The scrubber control: the volume brush over the whole scope, then a control
// row — window-size selector, volume-mode label, and the window's rank range +
// date span. While dragging, the date span is PROJECTED from the volume
// distribution (timeAtRankFrac) so the readout tracks the brush before the
// window fetches.
export default function ScanWindowScrubber({
  scrubbable = false, displayFrac = 1, windowFrac = 1, onSeek, commit,
  windowSize = 120, setWindowSize, sizeOptions = [],
  offset = 0, count = 0, total = 0, winMin, winMax, loading = false,
  dragging = false, scopePath = [], range = null,
  // Optional control rendered at the controls row's right edge, after the
  // rank label — the host's affordance for acting on the selected window
  // (e.g. the X-ray's "open these prompts" toggle).
  actions = null,
  // Optional chips rendered beside the Sample select — the host's active
  // window FILTERS, surfaced on the always-visible bar so a filter set
  // deep in a tab can be seen and cleared from anywhere.
  filters = null,
}) {
  const { data: volume, loading: volumeLoading } = useQuery(
    "getScanVolume",
    { scopePath, range, bucketCount: RANK_BARS },
    { staleMs: 60_000 },
  );
  // Backdrop metric: token volume, falling back to prompt counts inside
  // resampleToRank when the scope has no usage rows. (The metric picker
  // was cut — three near-identical bar shapes weren't worth the row
  // space, which the filter chips now use.)
  const { bars, mode } = useMemo(
    () => resampleToRank(volume, RANK_BARS, "tokens"),
    [volume],
  );

  // Committed window → real trace times; mid-drag → projected from the
  // cumulative volume distribution.
  const rightFrac = clamp01(displayFrac);
  const leftFrac = Math.max(0, rightFrac - windowFrac);
  const projFrom = dragging ? timeAtRankFrac(volume, leftFrac) : winMin;
  const projTo = dragging ? timeAtRankFrac(volume, rightFrac) : winMax;

  const pendingOffset = Math.min(Math.max(0, total - windowSize), Math.max(0, Math.round((1 - rightFrac) * total)));
  const fromRank = total ? (dragging ? pendingOffset : offset) + 1 : 0;
  const toRank = dragging ? Math.min(total, pendingOffset + windowSize) : offset + count;
  const rankLabel = `${fromRank.toLocaleString()}–${toRank.toLocaleString()}`;

  return (
    // Containment (border/background/padding) is the host's job — the
    // X-ray sits this inside a solid plot-bg bar on its Sheet's foot.
    <div className="grid gap-[6px]">
      <VolumeBrush
        posFrac={displayFrac}
        windowFrac={windowFrac}
        onSeek={onSeek}
        commit={commit}
        disabled={!scrubbable}
        bars={bars}
        mode={mode}
        loading={loading}
        volumeLoading={volumeLoading}
      />
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
        <label className="flex shrink-0 items-center gap-[6px]">
          <span className="text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]">Sample</span>
          <Select
            value={windowSize}
            onChange={(v) => setWindowSize?.(Number(v))}
            options={sizeOptions.map((n) => ({ value: n, label: String(n) }))}
            ariaLabel="Scan window size"
            align="left"
            triggerStyle={{ minHeight: 24, height: 24, paddingTop: 0, paddingBottom: 0 }}
            className="bg-[color:var(--control-bg)] px-2 text-[11px]"
          />
        </label>
        {filters}
        <span
          className={[
            "ml-auto whitespace-nowrap text-[10px] tracking-[0.04em] uppercase tabular-nums",
            loading || dragging ? "text-[color:var(--muted-soft)]" : "text-[color:var(--muted)]",
          ].join(" ")}
        >
          {rankLabel} of {total ? total.toLocaleString() : "?"}
          {projFrom != null ? ` · ${fmtTimeRange(projFrom, projTo)}` : ""}
        </span>
        {actions}
      </div>
    </div>
  );
}
